import React from 'react';
import { useIdioma } from '../i18n/idioma';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Estimate, EstimateItem, PricebookItem } from '../types';
import { useEstimateForm } from '../hooks/useEstimateForm';
import { computeEstimateTotals, type EstimateTotals } from '../services/estimateMath';
import { marginPairOf } from '../utils/formatMargin';
import { useSettings } from '../contexts/SettingsContext';
import { useNavigation } from '../contexts/NavigationContext';
import { useConfirm } from '../contexts/DialogContext';
import { useEstimates } from '../contexts/EstimatesContext';
import { getIndustryConfig } from '../data/industryConfig';
import EstimateLineItems from './estimate/EstimateLineItems';
import EstimateToolPanel from './estimate/EstimateToolPanel';
import EstimatePricingSnapshotPanel from './estimate/EstimatePricingSnapshotPanel';
import SiteConditionsForm from './estimate/SiteConditionsForm';
import SaveTemplatesPromptModal, { PromptableZone, PrivateTemplateSaveDecision } from './estimate/SaveTemplatesPromptModal';
import { limpiarDecisionesDePresupuesto } from './estimate/PrivateTemplateEditor';
import { findEstimateTemplateById } from '../data/categories/loader';
import { lookupCatalogItem } from '../data/categories/catalogLoader';
import { upsertWorkspaceCatalogPrice, addPrivateTemplate } from '../services/database';
import { ArrowLeft, Save, FileText, User, MapPin, ChevronDown, ChevronUp, Check, X, Clock, Hash, Plus, Settings2, AlertTriangle, Trash2 } from 'lucide-react';
import { formatCurrency } from '../utils/formatCurrency';
import AskRazaButton from './ai/AskRazaButton';
import { getFirstUnpricedItem, getFirstUnpricedItemId, countUnpricedItems } from '../utils/estimatePricingGaps';
import { firstVisible } from '../utils/domVisible';
import { isPricingConfigured } from '../utils/setupChecklist';
import { useDemoVisualActivo } from '../hooks/useDemoVisual';
import { useDebouncedField } from '../hooks/useDebouncedField';
import PricingNextStepCard from './estimate/PricingNextStepCard';
import { Button } from './ui/Button';

interface EstimateCalculatorProps {
    estimate: Estimate;
    onSave: (estimate: Estimate) => void;
    onSaveAndPreview?: (estimate: Estimate) => void;
    onClose: () => void;
    backLabel?: string;
}

/** Todo lo que `persistPricebookEdits` necesita del mundo exterior.
 *
 *  Va como parámetro en vez de importarse dentro para que la función quede
 *  pura: sin React, sin Supabase y sin el catálogo de 6.604 partidas. Esa es
 *  la única razón de que exista este tipo — permite probar la regla de
 *  contaminación (abajo) sin montar el constructor entero. */
export interface PricebookPersistDeps {
    /** Ids de las filas que YA están guardadas en la lista de precios del taller. */
    pricebookIds: Set<string>;
    lookupCatalogItem: (id: string) => { name: string; category: string; unit: string } | null;
    upsertWorkspaceCatalogPrice: (
        catalogItemId: string,
        cost: number,
        seed: { name: string; category: string; unit: string },
        markupPercent: number | null,
    ) => Promise<void>;
    addPricebookItem: (item: Omit<PricebookItem, 'id' | 'lastUpdated'>) => Promise<string>;
}

/** Persiste en la lista de precios del taller las correcciones de COSTO que el
 *  contratista hizo sobre filas que vinieron del catálogo global. El
 *  `sourcePricebookId` de cada fila es el id del catálogo, así que de ahí sale
 *  el sello (nombre/categoría/unidad) con el que se hace el upsert. Corre en
 *  paralelo; los fallos se registran pero nunca bloquean el guardado del
 *  estimado.
 *
 *  ── EL PORCENTAJE NO SE ESCRIBE. NUNCA. ────────────────────────────────────
 *  Invariante A.6.2 del plan de precios v2: ningún porcentaje derivado se
 *  escribe jamás en la lista de precios del taller. Esta función es justo por
 *  donde se rompía — **88 de las 110 filas contaminadas de producción entraron
 *  por aquí**, y mientras estuviera abierta, cualquier limpieza de datos se
 *  deshacía con el primer guardado.
 *
 *  El número que se colaba era 42,857%, y no lo tecleó nadie: es un margen del
 *  30% reescrito como markup. Margen = sobre PRECIO; markup = sobre COSTO. Un
 *  costo de $100 con 30% de margen se vende a 100/(1−0,30) = $142,86, y eso
 *  sobre el costo es 42,857% de markup — que devuelve el margen de partida:
 *  42,857/142,857 = 30,000%. Es el resultado de una fórmula, no una decisión.
 *
 *  Y una vez guardado deja de ser distinguible: quien lo lee en
 *  EstimateToolPanel ve un número y no puede saber si es el 60% que una
 *  persona tecleó o la resaca del dial de otro estimado. Peor, al soltar esa
 *  fila en el siguiente presupuesto el porcentaje derivado se vuelve a
 *  aplicar, y el error se compone.
 *
 *  El COSTO sí se sigue guardando: ese es el propósito documentado de esta
 *  función —que el precio que el contratista corrigió a mano sirva la próxima
 *  vez—. El porcentaje sale del dial del estimado, en cada cálculo, en su
 *  sitio. */
export async function persistPricebookEdits(
    currentItems: EstimateItem[],
    deps: PricebookPersistDeps,
): Promise<EstimateItem[]> {
    const { pricebookIds, lookupCatalogItem, upsertWorkspaceCatalogPrice, addPricebookItem } = deps;
    const next = [...currentItems];
    const ops: Promise<unknown>[] = [];

    next.forEach((i, idx) => {
        if (typeof i.cost !== 'number' || i.cost <= 0) return;

        // 1. Catalog-linked rows (template materials/equipment) → upsert the
        //    workspace price, keyed by catalog id (one row per catalog item).
        const catalogItem = i.sourcePricebookId ? lookupCatalogItem(i.sourcePricebookId) : null;
        if (catalogItem) {
            ops.push(
                upsertWorkspaceCatalogPrice(
                    i.sourcePricebookId!,
                    i.cost,
                    { name: catalogItem.name, category: catalogItem.category, unit: catalogItem.unit },
                    // Aquí iba `i.markupPercent ?? null`: la fuga. Ver la nota de
                    // arriba — el porcentaje de la LÍNEA no es un dato del taller.
                    null,
                ).catch(err => console.warn('[persistPricebookEdits] upsert failed', err)),
            );
            return;
        }

        // 2. Already a saved workspace pricebook row → nothing to add.
        if (i.sourcePricebookId && pricebookIds.has(i.sourcePricebookId)) return;

        // 3. New material (typed inline, or a template material whose catalog
        //    hint doesn't resolve) → add it to the material list so it's
        //    reusable next time. Duplicates are fine by design; stamp the new
        //    pricebook id back onto the row so repeat saves don't pile up copies.
        if (i.type === 'Material') {
            const cleanName = (i.description || 'Material').replace(/\s*⚠ catalog hint not found:.*$/, '').trim() || 'Material';
            ops.push(
                addPricebookItem({
                    name: cleanName,
                    cost: i.cost,
                    unit: i.unit || 'EA',
                    category: i.category || 'Other',
                    // NULL, y no 0 ni omitir la clave: `pricebook.markup_percent`
                    // acepta nulos pero su DEFAULT es 0, así que no mandar nada
                    // guardaría un 0 — indistinguible de un «0%» que alguien
                    // decidiera de verdad. NULL dice lo único cierto: aquí nadie
                    // tecleó un porcentaje, léelo del dial.
                    markupPercent: null,
                    vendor: '',
                    source: 'user',
                })
                    .then(newId => { next[idx] = { ...i, sourcePricebookId: newId }; })
                    .catch(err => console.warn('[persistPricebookEdits] addPricebookItem failed', err)),
            );
        }
    });

    await Promise.allSettled(ops);
    return next;
}

/** Lo poco que la cabecera necesita de los totales. Un `Pick` y no el objeto
 *  entero para que se vea, de un vistazo, que esta pieza no calcula nada. */
export type MargenCabeceraTotales = Pick<EstimateTotals,
    'marginPercent' | 'marginPercentOwnWork' | 'passThroughRevenue'>;

/**
 * El margen en la barra superior del constructor — las DOS lecturas (Parte A.5).
 *
 * QUÉ CAMBIA Y POR QUÉ:
 *
 *   · DOS NÚMEROS, NO UNO. Un permiso de $400 remitido a costo no deja un
 *     centavo de ganancia, pero mete $400 en el denominador y hunde el
 *     porcentaje facturado: el mismo trabajo lee 30,0% sobre lo propio y 28,0%
 *     facturado. El dial persigue el primero, así que es el que va grande; el
 *     segundo se menciona SOLO cuando difiere —enseñar «30,0% · 30,0%» no
 *     informa de nada y enseña a la gente a ignorar el segundo número—.
 *
 *   · UN DECIMAL, del formateador compartido. Aquí había un `.toFixed(0)`
 *     mientras la lista de estimados y el cajón de detalle usaban `.toFixed(1)`:
 *     el MISMO presupuesto decía 30% en el constructor y 29,6% en la lista.
 *
 *   · EL VEREDICTO SE MIDE CONTRA EL OBJETIVO DE ESTE ESTIMADO. Antes se
 *     comparaba contra `settings.pricing.targetMarginPercent`, que es el dial de
 *     la MANO DE OBRA —el porcentaje con el que se cotiza una fila de labor—, no
 *     la meta de un control agregado que incluye materiales, viaje y descuento.
 *     Sin objetivo propio no hay veredicto y el número va en negro: pintar de
 *     rojo un presupuesto al que nadie le puso meta es inventarse un suspenso.
 */
export const MargenCabecera: React.FC<{
    totals: MargenCabeceraTotales;
    /** El objetivo DEL ESTIMADO. `null` = nadie pidió nada, no hay veredicto. */
    objetivo: number | null;
}> = ({ totals, objetivo }) => {
    const { t } = useIdioma();
    const par = marginPairOf(totals);
    const real = Number.isFinite(totals.marginPercentOwnWork) ? totals.marginPercentOwnWork : 0;
    /* Medio dígito de holgura, que es lo que se pinta: así el color nunca
     * contradice al número que tiene al lado por polvo de coma flotante. */
    const enMeta = objetivo === null ? null : real >= objetivo - 0.05;
    const color = enMeta === null ? 'text-navy-950' : (enMeta ? 'text-exito' : 'text-aviso');

    return (
        <div className="text-right">
            <p className="label-ui">{t('estimado.constructor.margen')}</p>
            <p className={`text-lg font-data font-semibold leading-tight tabular-nums ${color}`}>
                {par.own}
            </p>
            {par.differs && (
                <p className="text-[10px] text-acero-500 leading-tight whitespace-nowrap">
                    <span>{t('herrEst.margen.propio')}</span>
                    {' · '}
                    <span className="font-data tabular-nums">{par.billed}</span>{' '}
                    <span>{t('herrEst.margen.facturado')}</span>
                </p>
            )}
        </div>
    );
};

const EstimateCalculator: React.FC<EstimateCalculatorProps> = ({ estimate, onSave, onSaveAndPreview, onClose, backLabel = 'Back' }) => {
    const { t } = useIdioma();
    /* `updateSettings` / `saveSettings` ya NO se piden aquí, y su ausencia es la
     * mitad de la tarea 4-A: el panel «Precio rápido» vivía en la cabecera de UN
     * estimado y escribía `settings.pricing.*`, que son los ajustes del TALLER
     * ENTERO — los de los compañeros y los de todos los estimados futuros. Los
     * diales globales se editan en Ajustes ▸ Precios y en ningún otro sitio; el
     * enlace «Abrir gastos y precios» del panel es la puerta. */
    const { settings } = useSettings();
    const { openSettings } = useNavigation();
    const { pricebook, addPricebookItem } = useEstimates();
    const qc = useQueryClient();
    const industryConfig = getIndustryConfig(settings.profile.industryType);
    const {
        items,
        adjustments,
        proposalSettings,
        snapshot,
        totals,
        setAdjustments,
        setProposalSettings,
        addItem,
        updateItem,
        removeItem,
        applyMargin,
        applyMaterialMarkup,
        refreshSnapshotFromCurrentSettings,
        // La política de precios DE ESTE ESTIMADO (decisión 2) y el candado de
        // los documentos que el cliente ya vio (decisión 7).
        pricingPolicy,
        setPricingPolicy,
        preciosEditables,
        // El dial de margen neto (Parte A.4). El constructor no lo calcula: lo
        // reparte entre quien lo enseña (la cabecera) y quien lo mueve (el panel).
        netMarginFactor,
        netMarginTarget,
        netMarginSolve,
        setNetMarginTarget,
        hayCambiosSinGuardar,
        // New Hooks
        siteConditions,
        setSiteConditions,
        zones,
        addZone,
        addTemplateItems,
        updateZone,
        removeZone
    } = useEstimateForm({ initialEstimate: estimate });

    /* El nombre del proyecto vive en `proposalSettings.title` — el mismo campo
     * que edita la pestaña Contenido de la propuesta. No se inventa uno nuevo a
     * propósito: ese título ya encabeza la propuesta, el PDF y el correo, así
     * que un segundo campo obligaría a escribir el nombre dos veces y a decidir
     * cuál gana.
     *
     * Con rebote porque cada tecla dispara el guardado sucio del constructor y
     * vuelve a dibujar el panel de herramientas entero. */
    const commitNombreProyecto = React.useCallback(
        (v: string) => setProposalSettings(prev => ({ ...prev, title: v })),
        [setProposalSettings],
    );
    const [nombreProyecto, setNombreProyecto] = useDebouncedField(proposalSettings.title || '', commitNombreProyecto);

    /* Relleno visual para capturas: se enciende con Ctrl/⌘+Shift+D y se va al
       recargar, porque vive solo en el estado de React. No existe fuera de
       desarrollo. */
    const demoVisual = useDemoVisualActivo();
    const [demoNombre, setDemoNombre] = React.useState<string | null>(null);
    const nombreCliente = demoNombre ?? estimate.clientName;

    /* Cerrojo: `StrictMode` corre los efectos DOS veces en desarrollo — que es
       justo donde vive esto — y sin él se sembraría todo duplicado. Va en un
       ref y no en estado para que no provoque otro render. */
    const yaSembrado = React.useRef(false);
    React.useEffect(() => {
        // La rama entera desaparece del build: en producción `DEV` es `false`,
        // y con ella se va el `import()` de los datos falsos.
        if (!import.meta.env.DEV) return;
        if (!demoVisual || yaSembrado.current) return;
        yaSembrado.current = true;
        void import('../hooks/demoVisualDatos').then(d => {
            setDemoNombre(d.DEMO_CLIENTE);
            d.DEMO_ZONAS.forEach(addZone);
            d.DEMO_ITEMS.forEach(addItem);
            setSiteConditions(d.DEMO_CONDICIONES);
            toast('Demo visual puesto — recarga para quitarlo. NO guardes.', { icon: '📸' });
        });
        // Solo al montar: sembrar en cada render duplicaría las líneas.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [demoVisual]);

    const [showSiteConditions, setShowSiteConditions] = React.useState(false);
    const [isAddingZone, setIsAddingZone] = React.useState(false);
    const [newZoneName, setNewZoneName] = React.useState('');
    const [showMobileTools, setShowMobileTools] = React.useState(false);
    const [promptZones, setPromptZones] = React.useState<PromptableZone[] | null>(null);
    const [pendingSaveMode, setPendingSaveMode] = React.useState<'save' | 'preview'>('save');
    const [showPricingPanel, setShowPricingPanel] = React.useState(false);
    // Styled confirmation for zone deletion — replaces the native browser confirm
    // for both entry points (chip × and line-items zone header trash button).
    const [deletingZone, setDeletingZone] = React.useState<{ id: string; name: string } | null>(null);
    const [alsoDeleteZoneItems, setAlsoDeleteZoneItems] = React.useState(false);

    const itemsInDeletingZone = React.useMemo(
        () => (deletingZone ? items.filter(i => i.zoneId === deletingZone.id) : []),
        [items, deletingZone],
    );

    const requestDeleteZone = (z: { id: string; name: string }) => {
        // Marcado por defecto: borrar una zona casi siempre significa borrar el
        // trabajo que había dentro. Con el defecto en `false` las líneas caían a
        // "No Zone" y había que borrarlas de una en una — 21 clics para deshacer
        // lo que el diálogo ya estaba preguntando. Desmarcar sigue conservándolas.
        setAlsoDeleteZoneItems(true);
        setDeletingZone(z);
    };
    const confirmDeleteZone = () => {
        if (!deletingZone) return;
        const { id, name } = deletingZone;
        const itemCount = itemsInDeletingZone.length;
        if (alsoDeleteZoneItems) {
            itemsInDeletingZone.forEach(i => removeItem(i.id));
        }
        removeZone(id);
        setDeletingZone(null);
        setAlsoDeleteZoneItems(false);
        toast.success(
            alsoDeleteZoneItems
                ? t('estimado.constructor.zona_borrada_con_partidas', { count: itemCount, name })
                : t('estimado.constructor.zona_borrada', { name })
        );
    };

    const { confirm } = useConfirm();

    /* Solo pregunta si de verdad hay algo que perder. Una confirmación que sale
       siempre es una que la gente aprende a despachar sin leer, y entonces deja
       de proteger justo el día que importa. */
    const salirDelEditor = async () => {
        if (hayCambiosSinGuardar) {
            const salir = await confirm({
                title: t('estimado.salir.titulo'),
                message: t('estimado.salir.mensaje'),
                confirmText: t('estimado.salir.confirmar'),
                cancelText: t('comun.cancelar'),
            });
            if (!salir) return;
        }
        onClose();
    };

    // Una sola definición de "los precios están configurados", en
    // utils/setupChecklist. La copia que vivía aquí era código muerto.
    const pricingNotConfigured = !isPricingConfigured(settings);

    const handleFocusUnpriced = React.useCallback(() => {
        setShowMobileTools(false);
        const firstId = getFirstUnpricedItemId(items);
        if (!firstId) return;
        requestAnimationFrame(() => {
            // `data-unpriced-item` es un atributo del DOM, no texto de interfaz.
            // Una pasada de traducción metió aquí una clave i18n y el selector
            // quedaba `[data-unpriced-partidas=…]` en español y
            // `[data-unpriced-items=…]` en inglés — no casaba en ningún idioma,
            // así que "llévame a la siguiente sin precio" no hacía nada.
            //
            // Y hay DOS nodos con ese atributo por fila —la tarjeta móvil y el
            // renglón de escritorio—, así que querySelector a secas devolvía
            // siempre la móvil, oculta en escritorio, donde scrollIntoView y
            // focus no hacen nada. firstVisible elige la que se ve.
            const nodes = Array.from(document.querySelectorAll<HTMLElement>(`[data-unpriced-item="${firstId}"]`));
            const el = firstVisible(nodes);
            el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            const input = el?.querySelector('input[type="number"]') as HTMLInputElement | null;
            input?.focus();
        });
    }, [items]);

    const unpricedCount = countUnpricedItems(items);
    const nextUnpriced = getFirstUnpricedItem(items);

    /**
     * Guardar un dial del panel «Precio rápido» escribe la política DE ESTE
     * ESTIMADO. Nada más.
     *
     * TRES COSAS HACÍA ANTES, DE TRES ALCANCES DISTINTOS, y por eso nadie podía
     * predecir qué iba a pasar al teclear un número (decisión 2 del plan):
     *
     *   1. Escribía `settings.pricing.*` —los ajustes del TALLER— desde la
     *      cabecera de un estimado. El compañero que tenía otro presupuesto
     *      abierto veía cambiar sus precios sin haber tocado nada.
     *   2. Guardaba contra la red, con su propio deshacer si fallaba.
     *   3. Re-cotizaba EN SILENCIO las partidas del documento abierto llamando a
     *      `applyMargin` / `applyMaterialMarkup`. Un contratista que solo quería
     *      apuntar «este trabajo va al 25%» se encontraba doce filas movidas.
     *
     * Ahora hace una sola: `setPricingPolicy`. Re-cotizar lo que ya está escrito
     * es un botón aparte, justo debajo, y hay que pulsarlo.
     */
    const handlePricingSave = (field: 'targetMarginPercent' | 'materialMarkupPercent', value: number) => {
        if (!preciosEditables) return;
        if (field === 'targetMarginPercent') {
            setPricingPolicy({ targetMarginPercent: value });
            toast.success(t('estimado.constructor.margen_aplicado', { value }));
            return;
        }
        setPricingPolicy({ materialMarkupPercent: value });
        toast.success(t('estimado.constructor.markup_aplicado', { value }));
    };

    /** La acción EXPLÍCITA: «aplica esta política también a las N partidas que ya
     *  están escritas». Es lo único que llama a `applyMargin` /
     *  `applyMaterialMarkup` en toda la app — ya no son el mecanismo de ningún
     *  deslizador ni de ningún guardado. Las dos respetan las filas fijadas a
     *  mano, así que un precio que el contratista decidió sobrevive al botón. */
    const aplicarPoliticaALasPartidas = () => {
        if (!preciosEditables || items.length === 0) return;
        applyMargin(pricingPolicy.targetMarginPercent);
        applyMaterialMarkup(pricingPolicy.materialMarkupPercent);
        toast.success(t('estimado.constructor.politica_aplicada', { count: items.length }));
    };

    // B-2: single canonical zone-creation path. Every entry point — the Project
    // Conditions inline input, the line-items "Add zone" button, and the new
    // Materials-tab "+ Add zone" beside the destination dropdown — routes through
    // here, so zones are created consistently and the caller gets the new id back.
    const createNamedZone = (name: string): string => {
        const id = Math.random().toString(36).substr(2, 9);
        addZone({ id, name });
        return id;
    };

    const handleAddZone = () => {
        if (newZoneName.trim()) {
            createNamedZone(newZoneName.trim());
            setNewZoneName('');
            setIsAddingZone(false);
        }
    };

    /* Cablea la función pura `persistPricebookEdits` (arriba de este archivo)
     * con el mundo real: el catálogo global, Supabase y el pricebook que ya
     * está en memoria. La lógica no vive aquí a propósito — dentro del
     * componente no se podía probar sin montar el constructor completo, y es
     * la lógica por la que se contaminaron 88 filas. */
    const persistirEdicionesPricebook = React.useCallback(
        (currentItems: EstimateItem[]) => persistPricebookEdits(currentItems, {
            pricebookIds: new Set(pricebook.map(p => p.id)),
            lookupCatalogItem,
            upsertWorkspaceCatalogPrice,
            addPricebookItem,
        }),
        [pricebook, addPricebookItem],
    );

    /** Compare current items in a zone against the template's original
     *  lineItems. Returns the count of "changed" lines — used to decide
     *  whether to prompt the contractor to save a private template. */
    const countChangedLines = (zoneItems: EstimateItem[], templateLineItems: any[]): number => {
        if (zoneItems.length !== templateLineItems.length) {
            // Added or removed lines count as fully changed.
            return Math.max(zoneItems.length, templateLineItems.length);
        }
        // Se compara contra el SELLO de cómo salió la fila, no contra el archivo
        // de la plantilla. El archivo no puede contestar "¿lo editó el
        // contratista?": declara unitPriceUsd 0 en las 6.604 partidas, mientras
        // que al soltar se aplica el precio del taller, así que comparar contra
        // él marcaba como cambiada toda fila de quien sí tiene precios cargados.
        //
        // Y buscar por descripción tampoco servía: cuando un hint no resuelve,
        // lineItemsToEstimateItems le pega un aviso al texto de la fila, así que
        // el find() fallaba y la contaba como cambiada.
        //
        // Sin sello no hay contra qué comparar —zona guardada antes de que el
        // sello existiera—. Se prefiere NO preguntar: el modal trae los
        // interruptores en `save: true` y acuñaría una plantilla que nadie pidió.
        if (zoneItems.some(i => i.dropQty === undefined || i.dropCost === undefined)) return 0;
        let changed = 0;
        for (const zi of zoneItems) {
            const qtyMatches = Math.abs((zi.dropQty ?? 0) - (zi.quantity ?? 0)) < 0.005;
            const costMatches = Math.abs((zi.dropCost ?? 0) - (zi.cost ?? 0)) < 0.005;
            if (!qtyMatches || !costMatches) changed++;
        }
        return changed;
    };

    /** Scan zones for those derived from a global template and return the ones
     *  whose contents have diverged from the original — those are the only
     *  zones worth prompting about. */
    const collectPromptableZones = (): PromptableZone[] => {
        const out: PromptableZone[] = [];
        for (const z of zones) {
            if (!z.sourceTemplateId) continue;
            const tpl = findEstimateTemplateById(z.sourceTemplateId);
            if (!tpl) continue;
            const zoneItems = items.filter(i => i.zoneId === z.id);
            if (zoneItems.length === 0) continue;
            const changedCount = countChangedLines(zoneItems, tpl.lineItems);
            if (changedCount === 0) continue;
            out.push({
                zoneId: z.id,
                zoneName: z.name,
                sourceTemplateId: z.sourceTemplateId,
                sourceTemplateName: tpl.name,
                itemCount: zoneItems.length,
                changedLineCount: changedCount,
                items: zoneItems,
            });
        }
        return out;
    };

    /** Final write step: persist the estimate via the parent's onSave. */
    const writeEstimate = (finalItems: EstimateItem[], mode: 'save' | 'preview' = 'save') => {
        // Recompute the grand total from the EXACT items being persisted (not the
        // render-closure totals, which were computed from pre-stamp items) so the
        // stored estimate.total can never disagree with its own line items.
        const finalTotal = computeEstimateTotals({
            items: finalItems,
            adjustments,
            siteConditions,
            pricingSnapshot: snapshot,
            rules: {
                tripCharge: settings.rules.tripCharge,
                emergencySurchargePercent: settings.rules.emergencySurchargePercent,
                minServiceCall: settings.rules.minServiceCall,
            },
        }).grandTotal;
        const payload = {
            ...estimate,
            items: finalItems,
            adjustments,
            proposalSettings,
            pricingSnapshot: snapshot,
            total: finalTotal,
            siteConditions,
            zones,
        };
        if (mode === 'preview' && onSaveAndPreview) {
            onSaveAndPreview(payload);
        } else {
            onSave(payload);
        }
    };

    /** Reflect pricebook-id stamps from persistPricebookEdits back into form state
     *  so re-saving the same estimate in this session doesn't re-add duplicates. */
    const syncPricebookStamps = (finalItems: EstimateItem[]) => {
        finalItems.forEach((fi, idx) => {
            if (fi.sourcePricebookId && fi.sourcePricebookId !== items[idx]?.sourcePricebookId) {
                updateItem(fi.id, { sourcePricebookId: fi.sourcePricebookId });
            }
        });
    };

    const finalizeSave = async (mode: 'save' | 'preview') => {
        const finalItems = await persistirEdicionesPricebook(items);
        syncPricebookStamps(finalItems);
        writeEstimate(finalItems, mode);
    };

    const handleSave = (mode: 'save' | 'preview' = 'save') => {
        const prompts = collectPromptableZones();
        if (prompts.length > 0) {
            setPendingSaveMode(mode);
            setPromptZones(prompts);
            return;
        }
        void finalizeSave(mode);
    };

    const handlePromptConfirm = async (decisions: PrivateTemplateSaveDecision[]) => {
        const toSave = decisions.filter(d => d.save);
        // Save private templates first — failures here are surfaced but don't
        // block the estimate save, since the estimate edits themselves are
        // independent of the template save.
        if (toSave.length > 0) {
            /* Las decisiones de precio de ESTE cliente no viajan dentro de la
             * plantilla.
             *
             * `d.items` son las partidas vivas de la zona, y llevan puestos el
             * pin (`pricePinned`, `pinnedPercent`, `pinnedMetric`) y el
             * `baseRate` que salió de la política de este presupuesto. Sin
             * limpiarlos, el 45% que el contratista le puso al material de UN
             * cliente quedaba congelado en la plantilla y se aplicaba a todos
             * los presupuestos futuros: el material de $20 entraba a $31,52 con
             * la insignia «45% markup» aunque la política de ese otro trabajo
             * dijera $24,00. Y como el `rate` guardado llevaba el factor `k` de
             * este estimado mientras el nuevo nace con k = 1, la fila entraba
             * violando la invariante A.6.4 desde el primer instante.
             *
             * Es la misma clase de contaminación que el 42,857% viajando al
             * pricebook: un valor local que se vuelve política global sin que
             * nadie lo decida. El editor de plantillas privadas ya limpiaba en
             * su propio guardado; esta ruta —la del modal al guardar el
             * presupuesto, que además viene marcada por defecto— no. */
            const results = await Promise.allSettled(toSave.map(d =>
                addPrivateTemplate(d.name, d.items.map(limpiarDecisionesDePresupuesto), d.sourceTemplateId)
            ));
            const ok = results.filter(r => r.status === 'fulfilled').length;
            const failed = results.length - ok;
            if (ok > 0) {
                toast.success(t('estimado.constructor.plantillas_guardadas', { count: ok }));
                // Refresh the Private tab so the new entries appear immediately.
                qc.invalidateQueries({ queryKey: ['privateTemplates'] });
            }
            if (failed > 0) toast.error(t('estimado.constructor.plantillas_fallaron', { count: failed }));
        }
        const finalItems = await persistirEdicionesPricebook(items);
        syncPricebookStamps(finalItems);
        setPromptZones(null);
        writeEstimate(finalItems, pendingSaveMode);
    };

    const handlePromptSkip = async () => {
        const finalItems = await persistirEdicionesPricebook(items);
        syncPricebookStamps(finalItems);
        setPromptZones(null);
        writeEstimate(finalItems, pendingSaveMode);
    };

    return (
        <div className="flex flex-col h-dvh bg-acero-50 overflow-hidden">

            {/* ═══ TOP BAR ═══ */}
            <div className="h-14 sm:h-16 px-3 sm:px-6 bg-white border-b border-acero-200 flex justify-between items-center shrink-0 z-20">
                {/* El grupo de la izquierda crece y se encoge; el de la derecha
                    nunca. Sin esto, el campo del nombre del proyecto le comía el
                    ancho a los botones de Guardar en pantallas chicas. */}
                <div className="flex items-center gap-4 flex-1 min-w-0">
                    {/* Salir con trabajo sin guardar PREGUNTA.
                        Las partidas, zonas y ajustes viven en memoria hasta que se
                        pulsa Guardar; antes este botón las tiraba todas de un toque,
                        sin aviso y sin deshacer. Y como el editor es una capa y no
                        una ruta, el botón Atrás del teléfono tampoco servía de red.

                        La etiqueta ya NO se esconde en móvil: era un ícono mudo,
                        idéntico a los retrocesos internos del panel de plantillas —
                        con la diferencia de que aquellos vuelven un nivel y este lo
                        borra todo. */}
                    <button
                        onClick={salirDelEditor}
                        className="shrink-0 flex items-center gap-2 px-3 h-9 text-acero-600 hover:text-navy-950 hover:bg-acero-100 rounded-xl transition-colors duration-150 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                        <ArrowLeft size={18} />
                        <span>{backLabel}</span>
                    </button>
                    <div className="h-8 w-px bg-acero-200 shrink-0" />
                    {/* El nombre del proyecto se teclea AQUÍ.
                        Antes este renglón era un rótulo fijo («Armar estimado»)
                        y el nombre solo se podía cambiar dentro del editor de la
                        propuesta, dos pantallas más adelante — así que todo
                        estimado salía llamándose «<Alcance> — <Cliente>», el
                        nombre que le pone useEstimateWorkflow al crearlo.
                        Es el MISMO campo (proposalSettings.title): lo que se
                        escriba aquí es lo que encabeza la propuesta y lo que
                        viaja como {project_title} en el correo al cliente. */}
                    <div className="min-w-0 flex-1 max-w-[340px]">
                        <input
                            className="w-full min-w-0 -ml-2 px-2 py-0.5 bg-transparent hover:bg-acero-100 focus:bg-white border border-transparent hover:border-acero-200 rounded-lg text-sm sm:text-base font-semibold tracking-tight text-navy-950 leading-tight truncate transition-[background-color,border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 placeholder-acero-400"
                            value={nombreProyecto}
                            onChange={(e) => setNombreProyecto(e.target.value)}
                            aria-label={t('estimado.constructor.nombre_proyecto')}
                            title={t('estimado.constructor.nombre_proyecto')}
                            placeholder={t('estimado.constructor.nombre_placeholder')}
                        />
                        <div className="hidden sm:flex items-center gap-2 text-xs text-acero-500 px-0.5">
                            <span className="flex items-center gap-1">
                                <User size={11} /> {nombreCliente}
                            </span>
                            <span className="text-acero-300">·</span>
                            <span className="font-data tabular-nums text-[11px] inline-flex items-center gap-0.5"><Hash size={10} />{estimate.id.slice(0, 8)}</span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                    <AskRazaButton
                        className="hidden sm:block"
                        contextLabel={t('estimado.constructor.contexto_ia', { nombreCliente })}
                        /* El contexto que recibe el modelo va en el idioma del
                           usuario: de él depende en qué idioma CONTESTA. Con
                           esto fijo en inglés, un contratista preguntaba en
                           español y le respondían en inglés. */
                        contextualFocus={t('estimado.constructor.contexto_focus', {
                            cliente: nombreCliente,
                            oficio: estimate.tradeId ? ` ${estimate.tradeId}.` : '',
                            alcance: estimate.scopeLabel ? ` ${estimate.scopeLabel}.` : '',
                            partidas: items.length,
                            total: formatCurrency(totals.grandTotal),
                            // El margen que el modelo lee es el del TRABAJO PROPIO —el
                            // que persigue el dial—, con un decimal como el resto de la
                            // app. El texto ya trae su propio «%», así que va sin él.
                            margen: (Number.isFinite(totals.marginPercentOwnWork) ? totals.marginPercentOwnWork : 0).toFixed(1),
                        })}
                        prompts={[
                            { label: t('estimado.constructor.ia_que_falta_label'), prompt: t('estimado.constructor.ia_que_falta_prompt') },
                            { label: t('estimado.constructor.ia_margen_label'), prompt: t('estimado.constructor.ia_margen_prompt') },
                            { label: t('estimado.constructor.ia_intro_label'), prompt: t('estimado.constructor.ia_intro_prompt') },
                        ]}
                    />
                    {/* El total, en pequeño, para teléfono y tablet.
                        Debajo de `lg` el bloque de la derecha se escondía entero
                        y el total desaparecía: agregabas una partida y tenías
                        que bajar hasta el final para ver cómo quedaba el
                        estimado. Es LA cifra que se está decidiendo en esta
                        pantalla; no puede ser la que no cabe.
                        Aquí va sola —sin rótulo y sin el margen— porque en 390
                        px la barra tiene sitio para un número y no para tres
                        cosas. El margen sigue arriba desde `lg`. */}
                    <div className="lg:hidden text-right mr-1 min-w-0">
                        <p className="text-[15px] font-data font-semibold text-navy-950 leading-tight tabular-nums">
                            {formatCurrency(totals.grandTotal)}
                        </p>
                    </div>

                    {/* Live total + margin */}
                    <div className="hidden lg:flex items-center gap-4 mr-2">
                        <div className="text-right">
                            <p className="label-ui">{t('comun.total')}</p>
                            <p className="text-lg font-data font-semibold text-navy-950 leading-tight tabular-nums">{formatCurrency(totals.grandTotal)}</p>
                        </div>
                        <div className="h-8 w-px bg-acero-200" />
                        <MargenCabecera totals={totals} objetivo={netMarginTarget} />
                        <div className="h-8 w-px bg-acero-200" />
                        <div className="text-right">
                            <p className="label-ui">{t('estimado.constructor.ganancia')}</p>
                            <p className={`text-lg font-data font-semibold leading-tight tabular-nums ${totals.netProfit >= 0 ? 'text-exito' : 'text-peligro'}`}>
                                {formatCurrency(totals.netProfit)}
                            </p>
                        </div>
                    </div>

                    <div className="relative">
                        <button
                            onClick={() => setShowPricingPanel(!showPricingPanel)}
                            className={`p-2 sm:p-2.5 rounded-xl transition-colors duration-150 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${showPricingPanel ? 'bg-navy-850 text-white' : 'bg-acero-100 text-acero-600 hover:bg-acero-200 hover:text-navy-950'}`}
                            title={t('estimado.constructor.precios_rapidos_tooltip')}
                        >
                            <Settings2 size={16} />
                        </button>

                        {showPricingPanel && (
                            <>
                                <div className="fixed inset-0 z-30" onClick={() => setShowPricingPanel(false)} />
                                {/* El panel de POLÍTICA DE ESTE ESTIMADO.
                                    Ya no hay campo de «cargo por viaje»: ese es un
                                    ajuste del taller (`settings.rules.tripCharge`) y
                                    no tiene nada que hacer en un panel cuyo alcance
                                    es un solo documento. Se edita en Ajustes ▸ Precios,
                                    a un clic del enlace de abajo. */}
                                <div className="absolute right-0 top-full mt-2 w-72 bg-white border border-acero-200 rounded-2xl shadow-s3 z-40 p-5 space-y-4 animate-in fade-in slide-in-from-top-2 duration-150">
                                    <h4 className="label-ui">{t('estimado.constructor.precios_rapidos_titulo')}</h4>
                                    {/* El rótulo dice a QUÉ afecta. Sin él, dos cajas
                                        de porcentaje en la cabecera de un estimado se
                                        leen como los ajustes del taller — que es lo
                                        que eran, y por eso hacía falta decirlo. */}
                                    <p className="text-[11px] text-acero-500">{t('estimado.constructor.precios_alcance')}</p>

                                    {!preciosEditables && (
                                        <p className="text-[11px] text-aviso bg-aviso-bg border border-aviso/25 rounded-xl px-3 py-2">
                                            {t('estimado.constructor.precios_bloqueados')}
                                        </p>
                                    )}

                                    <div>
                                        <label className="label-ui block mb-1.5" htmlFor="politica-margen">{t('estimado.constructor.margen_objetivo')}</label>
                                        <input
                                            id="politica-margen"
                                            type="number" min={0} max={80} step={1}
                                            disabled={!preciosEditables}
                                            className="w-full h-10 px-3 text-sm bg-white border border-acero-300 rounded-xl text-navy-950 font-data tabular-nums transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 disabled:bg-acero-100 disabled:text-acero-500"
                                            defaultValue={pricingPolicy.targetMarginPercent}
                                            onBlur={(e) => handlePricingSave('targetMarginPercent', Number(e.target.value) || 0)}
                                        />
                                    </div>

                                    <div>
                                        <label className="label-ui block mb-1.5" htmlFor="politica-markup">{t('estimado.constructor.markup_material')}</label>
                                        <input
                                            id="politica-markup"
                                            type="number" min={0} max={200} step={1}
                                            disabled={!preciosEditables}
                                            className="w-full h-10 px-3 text-sm bg-white border border-acero-300 rounded-xl text-navy-950 font-data tabular-nums transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55 disabled:bg-acero-100 disabled:text-acero-500"
                                            defaultValue={pricingPolicy.materialMarkupPercent}
                                            onBlur={(e) => handlePricingSave('materialMarkupPercent', Number(e.target.value) || 0)}
                                        />
                                    </div>

                                    {/* Re-cotizar lo que ya está escrito: un acto
                                        deliberado, con el número de filas a la vista.
                                        Antes ocurría solo al salir de una caja. */}
                                    {preciosEditables && items.length > 0 && (
                                        <Button variant="outline" className="w-full" onClick={aplicarPoliticaALasPartidas}>
                                            {t('estimado.constructor.aplicar_a_partidas', { count: items.length })}
                                        </Button>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => openSettings('Overhead')}
                                        className="text-xs font-medium text-navy-700 hover:text-navy-950 hover:underline underline-offset-4 pt-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded"
                                    >
                                        {t('estimado.constructor.abrir_gastos_precios')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <Button variant="outline" onClick={() => handleSave('save')} leftIcon={<Save size={16} />}>
                        {t('comun.guardar')}
                    </Button>
                    {onSaveAndPreview && (
                        <Button variant="primary" onClick={() => handleSave('preview')} leftIcon={<Save size={16} />}>
                            <span className="hidden sm:inline">{t('estimado.constructor.guardar_y_ver')}</span>
                            <span className="sm:hidden">{t('estimado.constructor.ver')}</span>
                        </Button>
                    )}
                </div>
            </div>

            {/* Pricing Warning Banner */}
            {pricingNotConfigured && (
                <div className="px-4 py-2.5 bg-aviso-bg border-b border-aviso/25 flex items-center justify-between gap-3 shrink-0 flex-wrap">
                    <div className="flex items-center gap-2 text-aviso text-xs font-medium">
                        <AlertTriangle size={14} />
                        <span>{t('estimado.constructor.aviso_precios')}</span>
                    </div>
                    {/* Un solo camino, y el correcto. El botón de «configuración
                        rápida» abría este panel, que desde la ola 4 ya NO escribe
                        los ajustes del taller: pulsarlo dejaba el aviso puesto
                        hiciera lo que hiciera el contratista. Lo que falta
                        configurar son gastos y precios del taller, y eso se
                        arregla en Ajustes. */}
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => openSettings('Overhead')}
                            className="text-xs font-semibold text-navy-700 hover:text-navy-950 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 rounded"
                        >
                            {t('estimado.constructor.config_completa')}
                        </button>
                    </div>
                </div>
            )}

            {/* Mobile View Toggle */}
            <div className="lg:hidden fixed bottom-[88px] mb-segura left-1/2 -translate-x-1/2 z-50">
                <button
                    onClick={() => setShowMobileTools(!showMobileTools)}
                    className="bg-navy-850 text-white px-6 h-11 rounded-xl font-semibold shadow-s3 flex items-center gap-2 hover:bg-navy-800 active:scale-[0.98] transition-colors duration-150 text-sm whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2"
                >
                    {showMobileTools ? (
                        <><FileText size={16} /> {t('estimado.constructor.ver_documento')}</>
                    ) : (
                        <><Plus size={16} /> {t('estimado.constructor.agregar_partidas')}</>
                    )}
                </button>
            </div>

            {/* ═══ MAIN WORKSPACE ═══ */}
            <div className="flex-1 flex overflow-hidden relative min-h-0">

                {/* ▌ LEFT COLUMN — Document View */}
                <div className={`flex-1 overflow-y-auto custom-scrollbar pb-32 lg:pb-0 ${showMobileTools ? 'hidden lg:block' : 'block'}`}>
                    <div className="max-w-5xl mx-auto p-4 sm:p-8 lg:p-10 space-y-6 lg:space-y-8">

                        {/* Site Conditions & Zones Card */}
                        {industryConfig.showSiteConditions && (
                        <div className="bg-white rounded-2xl border border-acero-200 overflow-hidden">
                            <button
                                onClick={() => setShowSiteConditions(!showSiteConditions)}
                                className="w-full flex justify-between items-center px-6 py-4 hover:bg-acero-50 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                            >
                                <div className="flex items-center gap-3">
                                    <MapPin size={16} className="text-acero-500 shrink-0" />
                                    <div className="text-left">
                                        <h3 className="font-semibold text-sm text-navy-950">{t('estimado.constructor.condiciones_titulo')}</h3>
                                        <p className="text-[11px] text-acero-500 mt-0.5">
                                            <span className="font-data tabular-nums">{zones.length}</span>{t('estimado.constructor.resumen_zonas')}<span className="font-data tabular-nums">{siteConditions.difficultyFactor}x</span>{t('estimado.constructor.resumen_dificultad')}{siteConditions.soilType}{t('estimado.constructor.resumen_suelo')}
                                            {siteConditions.weather && siteConditions.weather !== 'Normal' && ` · ${siteConditions.weather}`}
                                        </p>
                                    </div>
                                </div>
                                {showSiteConditions ? <ChevronUp size={18} className="text-acero-400" /> : <ChevronDown size={18} className="text-acero-400" />}
                            </button>

                            {showSiteConditions && (
                                <div className="p-6 border-t border-acero-100 animate-in slide-in-from-top-2">
                                    <SiteConditionsForm
                                        conditions={siteConditions}
                                        onChange={setSiteConditions}
                                    />

                                    {/* Zone Chips */}
                                    <div className="mt-6 pt-6 border-t border-acero-100">
                                        <h4 className="label-ui mb-3">{t('estimado.constructor.zonas_activas')}</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {zones.map(z => (
                                                <div key={z.id} className="group flex items-center bg-white border border-acero-200 rounded-lg pl-3 pr-1 h-9 hover:border-acero-300 transition-colors duration-150">
                                                    <span className="text-sm font-medium text-navy-950 mr-2">{z.name}</span>
                                                    <button
                                                        onClick={() => requestDeleteZone({ id: z.id, name: z.name })}
                                                        aria-label={t('estimado.constructor.borrar_zona_aria', { name: z.name })}
                                                        className="p-1 hover:bg-peligro-bg text-acero-400 hover:text-peligro rounded-md transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ))}
                                            {isAddingZone ? (
                                                <div className="flex items-center gap-1">
                                                    <input
                                                        type="text"
                                                        autoFocus
                                                        className="h-9 px-3 w-36 text-sm bg-white border border-acero-300 text-navy-950 rounded-xl transition-[border-color,box-shadow] duration-150 focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55"
                                                        placeholder={t('estimado.constructor.zona_nombre_placeholder')}
                                                        value={newZoneName}
                                                        onChange={(e) => setNewZoneName(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleAddZone();
                                                            if (e.key === 'Escape') setIsAddingZone(false);
                                                        }}
                                                    />
                                                    <button onClick={handleAddZone} aria-label={t('estimado.constructor.confirmar_zona')} className="p-2 text-navy-700 hover:bg-acero-100 hover:text-navy-950 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                                                        <Check size={14} />
                                                    </button>
                                                    <button onClick={() => setIsAddingZone(false)} aria-label={t('estimado.constructor.cancelar_zona')} className="p-2 text-acero-500 hover:bg-acero-100 hover:text-navy-950 rounded-lg transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400">
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <Button variant="outline" size="sm" onClick={() => setIsAddingZone(true)} leftIcon={<Plus size={14} />}>
                                                    {t('estimado.zona.agregar')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* El documento ya salió al cliente (decisión 7).
                            El aviso va aquí arriba, pegado a las partidas, y no
                            escondido en un panel: es donde el contratista va a
                            intentar cambiar un porcentaje y donde tiene que
                            enterarse de por qué no se mueve. El candado de verdad
                            está en `useEstimateForm`, que descarta todo campo de
                            precio en un estimado cerrado. */}
                        {!preciosEditables && (
                            <div className="px-4 py-2.5 bg-aviso-bg border border-aviso/25 rounded-2xl flex items-start gap-2">
                                <AlertTriangle size={14} className="text-aviso shrink-0 mt-0.5" />
                                <p className="text-xs text-aviso leading-snug">{t('estimado.constructor.precios_bloqueados')}</p>
                            </div>
                        )}

                        {unpricedCount > 0 && (
                            <PricingNextStepCard
                                unpricedCount={unpricedCount}
                                nextItemLabel={nextUnpriced?.description}
                                onShowNext={handleFocusUnpriced}
                            />
                        )}

                        {/* Line Items */}
                        <EstimateLineItems
                            items={items}
                            zones={zones}
                            /* El factor vigente, para que cada tarjeta pueda enseñar
                               «20% markup · ×1,113» en vez del porcentaje compuesto
                               que nadie tecleó (A.5). Con k = 1 no pinta nada. */
                            netMarginFactor={netMarginFactor}
                            /* Para que el campo de porcentaje se APAGUE en un
                       estimado cerrado. El candado real vive en el hook;
                       esto evita que la pantalla acepte lo que va a tirar. */
                            preciosEditables={preciosEditables}
                            onUpdate={updateItem}
                            onRemove={removeItem}
                            onAddZone={() => { setShowSiteConditions(true); setIsAddingZone(true); }}
                            onRemoveZone={(id) => {
                                const z = zones.find(zz => zz.id === id);
                                if (z) requestDeleteZone({ id: z.id, name: z.name });
                            }}
                        />

                        {/* Live Totals Summary */}
                        <div className="flex justify-end">
                            <div className="w-full max-w-md bg-white rounded-2xl border border-acero-200 p-6 space-y-3">
                                <div className="flex justify-between text-sm text-acero-600">
                                    <span>{t('estimado.constructor.total_partidas')}<span className="font-data tabular-nums">{items.length}</span>)</span>
                                    <span className="font-data tabular-nums font-medium text-navy-950">{formatCurrency(totals.itemsTotal)}</span>
                                </div>
                                {totals.tripFee > 0 && (
                                    <div className="flex justify-between text-sm text-acero-600">
                                        <span>{t('estimado.constructor.total_cargo_viaje')}</span>
                                        <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.tripFee)}</span>
                                    </div>
                                )}
                                {totals.laborSurcharge > 0 && (
                                    <div className="flex justify-between text-sm text-acero-600">
                                        <span>{t('estimado.constructor.total_recargo_emergencia')}</span>
                                        <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.laborSurcharge)}</span>
                                    </div>
                                )}
                                {totals.laborDifficultySurcharge > 0 && (
                                    <div className="flex justify-between text-sm text-aviso">
                                        <span>{t('estimado.constructor.total_dificultad')}<span className="font-data tabular-nums">{siteConditions.difficultyFactor.toFixed(2)}×</span>)</span>
                                        <span className="font-data tabular-nums">{formatCurrency(totals.laborDifficultySurcharge)}</span>
                                    </div>
                                )}
                                {totals.discountAmount > 0 && (
                                    <div className="flex justify-between text-sm text-peligro">
                                        <span>{t('estimado.constructor.total_descuento')}</span>
                                        <span className="font-data tabular-nums">-{formatCurrency(totals.discountAmount)}</span>
                                    </div>
                                )}
                                <div className="border-t border-acero-100 pt-2 flex justify-between text-sm text-navy-950">
                                    <span className="font-medium">Subtotal</span>
                                    <span className="font-data tabular-nums font-semibold">{formatCurrency(totals.subTotal)}</span>
                                </div>
                                {totals.finalMinFee > 0 && (
                                    <div className="flex justify-between text-sm text-acero-600">
                                        <span>{t('estimado.constructor.total_minimo')}</span>
                                        <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.finalMinFee)}</span>
                                    </div>
                                )}
                                {totals.taxAmount > 0 && (
                                    <div className="flex justify-between text-sm text-acero-600">
                                        <span>{t('estimado.constructor.total_impuesto')}<span className="font-data tabular-nums">{adjustments.taxRate}%</span>)</span>
                                        <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.taxAmount)}</span>
                                    </div>
                                )}
                                <div className="border-t border-acero-200 pt-3 flex justify-between items-end">
                                    <span className="label-ui">{t('estimado.constructor.total_estimado')}</span>
                                    <span className="text-3xl font-data font-semibold text-navy-950 tracking-tight tabular-nums">{formatCurrency(totals.grandTotal)}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>

                {/* ▌ RIGHT COLUMN — Toolbox Panel */}
                <div className={`${showMobileTools ? 'flex' : 'hidden lg:flex'} flex-col w-full lg:w-auto flex-1 lg:flex-none min-h-0 bg-white`}>
                    <div className="px-4 pt-3 pb-2 border-b border-acero-100 bg-acero-50 shrink-0">
                        <EstimatePricingSnapshotPanel
                            snapshot={snapshot}
                            status={estimate.status}
                            onRefresh={refreshSnapshotFromCurrentSettings}
                            defaultCollapsed
                        />
                    </div>
                    <div className="flex-1 min-h-0 flex flex-col">
                    <EstimateToolPanel
                        items={items}
                        onAddItem={addItem}
                        onAddTemplateItems={addTemplateItems}
                        adjustments={adjustments}
                        setAdjustments={setAdjustments}
                        proposalSettings={proposalSettings}
                        setProposalSettings={setProposalSettings}
                        totals={totals}
                        snapshot={snapshot}
                        settings={settings}
                        /* `applyMargin` y `targetMarginGoal` ya no viajan hasta
                           aquí: re-cotizar las partidas existentes es el botón
                           del panel de política, y dejar la función a mano en el
                           panel del deslizador es cómo se volvió a enganchar al
                           gesto la última vez. */
                        preciosEditables={preciosEditables}
                        netMarginFactor={netMarginFactor}
                        netMarginTarget={netMarginTarget}
                        netMarginSolve={netMarginSolve}
                        setNetMarginTarget={setNetMarginTarget}
                        siteConditions={siteConditions}
                        zones={zones}
                        onCreateZone={createNamedZone}
                        initialTradeId={estimate.tradeId}
                        initialScopeCategoryId={estimate.scopeCategoryId}
                        businessState={settings.profile.state}
                        onFocusUnpriced={handleFocusUnpriced}
                    />
                    </div>
                </div>

            </div>

            {/* ═══ BOTTOM STATUS BAR — hidden on mobile ═══ */}
            <div className="hidden sm:flex h-10 px-6 bg-white border-t border-acero-200 justify-between items-center shrink-0 text-xs">
                <div className="flex items-center gap-3 text-acero-500">
                    <div className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-brand-400 shrink-0" aria-hidden="true" />
                        <span><span className="font-data tabular-nums">{items.length}</span>{t('estimado.constructor.barra_partidas')}</span>
                    </div>
                    <span className="text-acero-200">|</span>
                    <span className="flex items-center gap-1">
                        <Clock size={11} /> <span className="font-data tabular-nums">{new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                    </span>
                </div>
                <div className="flex items-center gap-2 text-acero-500">
                    <span>{t('estimado.constructor.barra_costo')} <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.totalCost)}</span></span>
                    <span className="text-acero-200">|</span>
                    <span>{t('estimado.constructor.barra_ingreso')} <span className="font-data tabular-nums text-navy-950">{formatCurrency(totals.subTotal + totals.finalMinFee)}</span></span>
                    <span className="text-acero-200">|</span>
                    <span>{t('estimado.constructor.barra_ganancia')} <span className={`font-data tabular-nums font-medium ${totals.netProfit >= 0 ? 'text-exito' : 'text-peligro'}`}>{formatCurrency(totals.netProfit)}</span></span>
                </div>
            </div>

            {promptZones && (
                <SaveTemplatesPromptModal
                    zones={promptZones}
                    onConfirm={handlePromptConfirm}
                    onSkip={handlePromptSkip}
                    onClose={() => setPromptZones(null)}
                />
            )}

            {/* Delete Zone Confirmation */}
            {deletingZone && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-navy-950/50"
                        onClick={() => setDeletingZone(null)}
                    />
                    <div className="relative bg-white rounded-3xl shadow-s3 w-full max-w-md p-6 animate-in fade-in zoom-in duration-150">
                        <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-xl bg-peligro-bg text-peligro grid place-items-center shrink-0">
                                <AlertTriangle size={18} />
                            </div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-[17px] font-semibold tracking-tight text-navy-950">{t('estimado.constructor.borrar_zona_titulo')}</h3>
                                <p className="text-sm text-acero-500 mt-1.5">
                                    {t('estimado.constructor.borrar_zona_texto')}{' '}
                                    <span className="font-medium text-navy-950">"{deletingZone.name}"</span>.
                                </p>
                                {itemsInDeletingZone.length > 0 ? (
                                    <label className="mt-3 flex items-start gap-2.5 p-3 rounded-xl bg-acero-50 border border-acero-200 cursor-pointer hover:bg-acero-100 transition-colors duration-150">
                                        <input
                                            type="checkbox"
                                            checked={alsoDeleteZoneItems}
                                            onChange={(e) => setAlsoDeleteZoneItems(e.target.checked)}
                                            className="mt-0.5 w-4 h-4 rounded accent-navy-850 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                        />
                                        <span className="text-xs text-acero-600">
                                            {t('estimado.constructor.borrar_zona_check1')}<span className="font-data tabular-nums font-medium text-navy-950">{itemsInDeletingZone.length}</span> <span className="font-medium text-navy-950">{t('estimado.constructor.borrar_zona_check_partida', { count: itemsInDeletingZone.length })}</span>{t('estimado.constructor.borrar_zona_check2')}
                                            <span className="block text-[11px] text-acero-500 mt-0.5">
                                                {t('estimado.constructor.borrar_zona_conservar')}<span className="font-medium text-navy-950">{t('estimado.constructor.sin_zona')}</span>{t('estimado.constructor.borrar_zona_conservar2')}
                                            </span>
                                        </span>
                                    </label>
                                ) : (
                                    <p className="text-xs text-acero-500 mt-2">
                                        {t('estimado.constructor.zona_sin_partidas')}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2 mt-6">
                            <Button variant="outline" className="flex-1" onClick={() => setDeletingZone(null)}>
                                {t('comun.cancelar')}
                            </Button>
                            <Button variant="danger" className="flex-1" onClick={confirmDeleteZone} leftIcon={<Trash2 size={15} />}>
                                {t('estimado.zona.borrar')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EstimateCalculator;